// 3rd Party
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';

// Components
import { DIRECTION } from 'pages/common/components/Autocomplete/types';

import { COUNTRY } from '../../../../../common/constants';
import {
  Div,
  H1,
  H2,
  P,
} from '../../../../../common/styles/oc-styled-components';
import { colors } from '../../../../../common/styles/style-constants';
import {
  selectDataClinic,
  selectDataRequestedAppointment,
} from '../../../../../connect/selectors';
import {
  DIMENSIONS,
  useWindowDimensions,
} from '../../../../../providers/WindowDimensionsProvider';
import FloatingInput from '../../../../common/components/FloatingInput/FloatingInput';
import InsurancePickerCanada from '../../../../common/components/InsurancePickerCanada/InsurancePickerCanada';
import InsurancePickerUS from '../../../../common/components/InsurancePickerUS/InsurancePickerUS';
import {
  NameBirthContainer,
  ResponsiveInputRow,
  StyledLock,
} from '../../../checkout-styled-components';
import InsuranceCardUploader from '../../patient/InsuranceStep/InsuranceCardUploader';
import {
  CustomStyledAlert,
  Fieldset,
  MemberIdModal,
  MobileAlert,
  OpacityDiv,
  SageLink,
} from '../../patient/InsuranceStep/styled-components';
import { BirthDateFormRow } from '../BirthdateFormRow/BirthdateFormRow';
import MemberIDHelperModal from '../MemberIDHelperModal/MemberIDHelperModal';
import { NameFormRow } from '../NameFormRow/NameFormRow';
import { PrimaryMemberSwitchFormRow } from '../PrimaryMemberSwitch/PrimaryMemberSwitch';

import { InsuranceWarning } from './InsuranceWarning/InsuranceWarning';

const isPlanInNetwork = ({
  insuranceProviderId,
  selectedPlanId,
  supportedPlans,
}) => {
  if (!insuranceProviderId) {
    return false;
  }

  return supportedPlans.some(
    plan =>
      plan.provider.id === insuranceProviderId
      && (!selectedPlanId || plan.id === selectedPlanId),
  );
};

export const ContextType = Object.freeze({
  PATIENT: 'patient',
  PATIENT_SPLIT_STEPS: 'patient-split-steps',
  PARTNER: 'partner',
});

export const CheckoutInsuranceForm = ({
  insuranceProvider,
  insuranceMemberId,
  insuranceEmployer,
  insurancePlan,
  insuranceForm,
  checkRequiredFields,
  country,
  handleUpdateInsurance,
  insuranceCardEnabled,
  checkMemberId,
  patient,
  contextType,
  groupNumber,
  memberInformation,
  onMemberInformationChange,
  isMemberIdOptional,
}) => {
  const requestedAppointment = useSelector(selectDataRequestedAppointment);
  const clinic = useSelector(selectDataClinic);
  const [isMemberIDHelperVisible, setIsMemberIDHelperVisible] = useState(false);
  const { layout } = useWindowDimensions();

  const shouldShowInsuranceWarning = (() => {
    if (country !== COUNTRY.unitedStates || !insurancePlan?.id) {
      return false;
    }

    const clinicAcceptsOutOfNetworkPatients = !clinic.metadata
      .inNetworkOnlyFeature;

    if (clinicAcceptsOutOfNetworkPatients) {
      return false;
    }

    return !isPlanInNetwork({
      insuranceProviderId: insuranceProvider?.id,
      selectedPlanId: insurancePlan?.id,
      supportedPlans: requestedAppointment.provider.doctor.insurancePlans,
    });
  })();

  const hasInsuranceProvider = !!insuranceProvider;

  const memberIdMessage = (
    <P>
      Member ID usually follows the format ABC123456789 or 123456789.{' '}
      <SageLink onClick={() => setIsMemberIDHelperVisible(true)} tabIndex="0">
        Help me find it
      </SageLink>
    </P>
  );

  return (
    <section>
      <Fieldset
        data-testid="insuranceInformationSection"
        margin={
          contextType === ContextType.PATIENT_SPLIT_STEPS
            ? '-1.5em 0 0 0'
            : undefined
        }
      >
        <H1 fontSize="20px" margin="0" fontWeight="600">
          Insurance Information
        </H1>
        <P margin=" 0 0 8px">
          This information will be sent to the practice so they can check your
          coverage.
        </P>
        {country === COUNTRY.unitedStates
          ? (
            <>
              <InsurancePickerUS
                defaultProvider={insuranceProvider}
                defaultPlan={(insurancePlan || {}).id}
                form={insuranceForm}
                updateInsurance={(newProvider, newPlan) =>
                  handleUpdateInsurance(newProvider, newPlan)
                }
                checkRequiredFields={checkRequiredFields}
              />
              {shouldShowInsuranceWarning
                && (
                  <InsuranceWarning
                    insuranceProvider={insuranceProvider}
                    insurancePlan={insurancePlan}
                  />
                )
              }
            </>
          )
          : (
            <InsurancePickerCanada
              preferredDropDirection={
                layout === DIMENSIONS.MOBILE
                  ? DIRECTION.DOWN
                  : DIRECTION.UP
              }
              defaultProvider={insuranceProvider?.name}
              form={insuranceForm}
              updateInsurance={(newProvider, newPlan) =>
                handleUpdateInsurance(newProvider, newPlan)
              }
              checkRequiredFields={checkRequiredFields}
            />
          )
        }
        {!shouldShowInsuranceWarning
          && (
            <>
              {contextType === ContextType.PATIENT_SPLIT_STEPS
                ? <MobileAlert>{memberIdMessage}</MobileAlert>
                : (
                  <CustomStyledAlert margin="6px 0">
                    {memberIdMessage}
                  </CustomStyledAlert>
                )
              }
              {(contextType !== ContextType.PATIENT_SPLIT_STEPS
                || hasInsuranceProvider)
              && (
                <FloatingInput
                  field="memberId"
                  label={`Member ID${isMemberIdOptional ? ' (Optional)' : ''}`}
                  sensitive={false}
                  mask
                  required={!isMemberIdOptional}
                  testId="memberIdInput"
                  validator={checkMemberId}
                  initialValue={insuranceMemberId}
                  onChange={e =>
                    onMemberInformationChange('memberId', e.target.value)
                  }
                />
              )
              }
              <ResponsiveInputRow>
                {country === COUNTRY.unitedStates
                  && (
                    <Div
                      margin={
                        contextType === ContextType.PATIENT_SPLIT_STEPS
                          ? undefined
                          : '0 15px 0 0'
                      }
                      width="100%"
                    >
                      <FloatingInput
                        field="groupNumber"
                        label="Group ID (Optional)"
                        testId="groupNumberInput"
                        initialValue={groupNumber}
                        onChange={e =>
                          onMemberInformationChange('groupNumber', e.target.value)
                        }
                      />
                    </Div>
                  )
                }
                {
                  (contextType === ContextType.PATIENT
                    || contextType === ContextType.PARTNER)
                  && country === COUNTRY.unitedStates
                  && (
                    <Div width="100%">
                      <FloatingInput
                        field="employer"
                        label="Employer (Optional)"
                        message="Please enter your employer"
                        testId="employerStepInput"
                        initialValue={insuranceEmployer}
                        onChange={e =>
                          onMemberInformationChange('employer', e.target.value)
                        }
                      />
                    </Div>
                  )

                }
              </ResponsiveInputRow>

              {insuranceCardEnabled
                && (
                  <InsuranceCardUploader
                    margin={
                      contextType === ContextType.PATIENT_SPLIT_STEPS
                        ? '0 0 15px'
                        : '0 0 20px'
                    }
                  />
                )
              }

              <PrimaryMemberSwitchFormRow
                isPrimaryMember={memberInformation.isPrimaryMember}
                setPrimaryMember={value =>
                  onMemberInformationChange('isPrimaryMember', value)
                }
              />
            </>
          )
        }
      </Fieldset>
      {!memberInformation.isPrimaryMember && !shouldShowInsuranceWarning
        && (
          <Fieldset
            data-testid="memberInformation"
            margin={
              contextType === ContextType.PATIENT_SPLIT_STEPS
                ? '-15px 0 11px'
                : '0 0 22px'
            }
          >
            <H2
              color={colors.charcoal}
              fontSize="20px"
              margin="0"
              fontWeight="600"
            >
              Member Information
              <StyledLock
                aria-hidden
                bottom="5px"
                margin="0 0 0 8px"
                size="16px"
                theme="filled"
                type="lock"
              />
            </H2>
            <P
              color={colors.slate}
              margin={
                contextType === ContextType.PATIENT_SPLIT_STEPS
                  ? '0 0 10px'
                  : '0 0 20px'
              }
            >
              Who is the primary member / subscriber?
            </P>
            <NameBirthContainer flex={hasInsuranceProvider}>
              <NameFormRow
                prefix="member"
                firstName={memberInformation.memberfirstName}
                lastName={memberInformation.memberlastName}
                onChange={onMemberInformationChange}
              />
              <BirthDateFormRow
                prefix="member"
                day={memberInformation.memberbirthDay}
                month={memberInformation.memberbirthMonth}
                year={memberInformation.memberbirthYear}
                onChange={onMemberInformationChange}
              />
            </NameBirthContainer>
          </Fieldset>
        )
      }

      {[ContextType.PATIENT_SPLIT_STEPS, ContextType.PATIENT].includes(
        contextType,
      )
      && (
        <OpacityDiv className={isMemberIDHelperVisible ? 'hidden' : null}>
          <MemberIdModal
            onCancel={() => setIsMemberIDHelperVisible(false)}
            onOk={() => setIsMemberIDHelperVisible(false)}
            visible={isMemberIDHelperVisible}
          >
            <MemberIDHelperModal
              insuranceProvider={insuranceProvider?.name}
              patient={patient}
              country={country}
            />
          </MemberIdModal>
        </OpacityDiv>
      )
      }
    </section>
  );
};

CheckoutInsuranceForm.propTypes = {
  insuranceForm: PropTypes.object.isRequired,
  checkRequiredFields: PropTypes.func.isRequired,
  country: PropTypes.string.isRequired,
  handleUpdateInsurance: PropTypes.func.isRequired,
  insuranceCardEnabled: PropTypes.bool.isRequired,
  checkMemberId: PropTypes.func.isRequired,
  contextType: PropTypes.oneOf(Object.values(ContextType)).isRequired,

  // Optional props
  insuranceProvider: PropTypes.object,
  insurancePlan: PropTypes.object,
  insuranceMemberId: PropTypes.string,
  insuranceEmployer: PropTypes.string,
  patient: PropTypes.object,
  groupNumber: PropTypes.string,
  memberInformation: PropTypes.object,
  onMemberInformationChange: PropTypes.func,
  isMemberIdOptional: PropTypes.bool,
};
